// RETICOLO QUANTISTICO — il fondo animato della home. Nessun file video: un canvas 2D di
// poche decine di kilobyte, che parte all'istante, non consuma banda e non ha licenze.
//
// Tre modi:
//   "lattice"  reticolo regolare che respira: e' la struttura del problema (il lattice su
//              cui poggia ML-DSA). Impulsi che corrono lungo gli spigoli.
//   "mesh"     nodi sparsi che si trovano fra loro: la rete P2P che si autoorganizza.
//   "orbit"    pochi nodi che ruotano attorno a un centro: usato nelle sezioni strette.
//
// Rispetta `prefers-reduced-motion` (disegna un fotogramma e si ferma) e si mette in pausa
// quando esce dalla viewport: una pagina non deve scaldare il portatile di chi la legge.
import React, { useEffect, useRef } from "react";

export default function QuantumLattice({ mode = "lattice", density = 1, className = "", interactive = true, opacity = 1 }) {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let raf = 0, w = 0, h = 0, dpr = 1, t = 0, visible = true, stopped = false;
    const mouse = { x: -9999, y: -9999 };
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    const tint = () => (getComputedStyle(document.documentElement).getPropertyValue("--lt-primary") || "#50C878").trim();
    const rgb = () => (getComputedStyle(document.documentElement).getPropertyValue("--lt-primary-rgb") || "80 200 120").trim().split(/\s+/).join(",");

    let nodes = [], edges = [], pulses = [];

    function build() {
      nodes = []; edges = []; pulses = [];
      if (mode === "lattice") {
        const step = Math.max(74, Math.min(132, w / (9 * density)));
        const cols = Math.ceil(w / step) + 2, rows = Math.ceil(h / step) + 2;
        for (let i = 0; i < cols; i++) {
          for (let j = 0; j < rows; j++) {
            const jx = (Math.sin(i * 12.9898 + j * 78.233) * 43758.5453) % 1;
            const jy = (Math.sin(i * 39.3468 + j * 11.135) * 24634.6345) % 1;
            nodes.push({
              bx: (i - 1) * step + jx * step * 0.34,
              by: (j - 1) * step + jy * step * 0.34,
              x: 0, y: 0,
              ph: (jx + jy) * 6.28,
              r: 0.9 + Math.abs(jx) * 1.1,
            });
          }
        }
        for (let i = 0; i < cols; i++) {
          for (let j = 0; j < rows; j++) {
            const a = i * rows + j;
            if (j + 1 < rows) edges.push([a, a + 1]);
            if (i + 1 < cols) edges.push([a, a + rows]);
          }
        }
      } else {
        const n = mode === "orbit" ? Math.round(48 * density) : Math.max(16, Math.round((w * h) / 13000 * density));
        for (let i = 0; i < n; i++) {
          const ang = (i / n) * Math.PI * 2;
          nodes.push({
            bx: mode === "orbit" ? w / 2 + Math.cos(ang) * (Math.min(w, h) * (0.16 + (i % 3) * 0.11)) : Math.random() * w,
            by: mode === "orbit" ? h / 2 + Math.sin(ang) * (Math.min(w, h) * (0.16 + (i % 3) * 0.11)) : Math.random() * h,
            x: 0, y: 0,
            vx: mode === "orbit" ? 0 : (Math.random() - 0.5) * 0.16,
            vy: mode === "orbit" ? 0 : (Math.random() - 0.5) * 0.16,
            ang, orb: 0.00016 + (i % 5) * 0.00007,
            ph: Math.random() * 6.28,
            r: 1 + Math.random() * 1.3,
          });
        }
      }
      for (let i = 0; i < Math.max(6, Math.round((mode === "lattice" ? edges.length / 90 : nodes.length / 4))); i++) spawn();
    }

    function spawn() {
      if (mode === "lattice") {
        if (!edges.length) return;
        pulses.push({ e: (Math.random() * edges.length) | 0, p: Math.random(), sp: 0.0022 + Math.random() * 0.0042 });
      } else if (nodes.length > 1) {
        const a = (Math.random() * nodes.length) | 0;
        let b = (Math.random() * nodes.length) | 0;
        if (b === a) b = (a + 1) % nodes.length;
        pulses.push({ a, b, p: Math.random(), sp: 0.0035 + Math.random() * 0.006 });
      }
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const box = cv.getBoundingClientRect();
      w = Math.max(1, box.width); h = Math.max(1, box.height);
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    }

    function frame() {
      if (stopped) return;
      t += reduce ? 0 : 1;
      const col = tint(), c3 = rgb();
      ctx.clearRect(0, 0, w, h);

      for (const n of nodes) {
        if (mode === "orbit") {
          const cx = w / 2, cy = h / 2;
          const dx = n.bx - cx, dy = n.by - cy;
          const a = n.orb * t;
          n.x = cx + dx * Math.cos(a) - dy * Math.sin(a);
          n.y = cy + dx * Math.sin(a) + dy * Math.cos(a);
        } else if (mode === "mesh") {
          n.bx += n.vx; n.by += n.vy;
          if (n.bx < 0 || n.bx > w) n.vx *= -1;
          if (n.by < 0 || n.by > h) n.vy *= -1;
          n.x = n.bx; n.y = n.by;
        } else {
          n.x = n.bx + Math.sin(t * 0.006 + n.ph) * 5.5;
          n.y = n.by + Math.cos(t * 0.005 + n.ph * 1.3) * 5.5;
        }
        if (interactive) {
          const dx = n.x - mouse.x, dy = n.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 26000) {
            const f = (1 - d2 / 26000) * 16;
            const d = Math.sqrt(d2) || 1;
            n.x += (dx / d) * f; n.y += (dy / d) * f;
          }
        }
      }

      ctx.lineWidth = 1;
      if (mode === "lattice") {
        ctx.strokeStyle = `rgba(${c3},0.11)`;
        ctx.beginPath();
        for (const [a, b] of edges) {
          ctx.moveTo(nodes[a].x, nodes[a].y);
          ctx.lineTo(nodes[b].x, nodes[b].y);
        }
        ctx.stroke();
      } else {
        const lim = mode === "orbit" ? 150 : 168;
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
            const d = Math.hypot(dx, dy);
            if (d < lim) {
              ctx.strokeStyle = `rgba(${c3},${(1 - d / lim) * 0.2})`;
              ctx.beginPath();
              ctx.moveTo(nodes[i].x, nodes[i].y);
              ctx.lineTo(nodes[j].x, nodes[j].y);
              ctx.stroke();
            }
          }
        }
      }

      for (const n of nodes) {
        const b = 0.3 + 0.34 * (0.5 + 0.5 * Math.sin(t * 0.02 + n.ph));
        ctx.fillStyle = `rgba(${c3},${b})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, 6.2832);
        ctx.fill();
      }

      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        let ax, ay, bx, by;
        if (mode === "lattice") {
          const e = edges[p.e]; if (!e) { pulses.splice(i, 1); continue; }
          ax = nodes[e[0]].x; ay = nodes[e[0]].y; bx = nodes[e[1]].x; by = nodes[e[1]].y;
        } else {
          const a = nodes[p.a], b = nodes[p.b];
          if (!a || !b) { pulses.splice(i, 1); continue; }
          ax = a.x; ay = a.y; bx = b.x; by = b.y;
        }
        if (!reduce) p.p += p.sp;
        if (p.p > 1) { pulses.splice(i, 1); spawn(); continue; }
        const x = ax + (bx - ax) * p.p, y = ay + (by - ay) * p.p;
        const g = ctx.createRadialGradient(x, y, 0, x, y, 13);
        g.addColorStop(0, col);
        g.addColorStop(1, `rgba(${c3},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, 13, 0, 6.2832);
        ctx.fill();
      }

      if (!reduce && visible) raf = requestAnimationFrame(frame);
    }

    const onMove = (e) => {
      const box = cv.getBoundingClientRect();
      mouse.x = e.clientX - box.left; mouse.y = e.clientY - box.top;
    };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };

    const io = new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
      if (visible && !reduce) { cancelAnimationFrame(raf); raf = requestAnimationFrame(frame); }
      else cancelAnimationFrame(raf);
    }, { rootMargin: "120px" });

    const ro = new ResizeObserver(() => resize());
    resize();
    frame();
    io.observe(cv);
    ro.observe(cv);
    if (interactive) { window.addEventListener("pointermove", onMove, { passive: true }); window.addEventListener("pointerleave", onLeave); }
    const onTheme = () => {};
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      io.disconnect(); ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("lt-theme", onTheme);
    };
  }, [mode, density, interactive]);

  return <canvas ref={ref} aria-hidden="true" style={{ opacity }} className={`pointer-events-none block h-full w-full ${className}`} data-testid={`quantum-lattice-${mode}`} />;
}
